import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  useTheme,
  alpha,
  Rating,
  Chip,
} from '@mui/material';
import {
  KeyboardArrowLeft as ArrowLeftIcon,
  KeyboardArrowRight as ArrowRightIcon,
  ArrowOutward as ArrowOutwardIcon,
  People as PeopleIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import API from '../api/axios';
import { getImageUrl, FALLBACK_IMAGE } from '../utils/imageUtils';
import useCategories from '../hooks/useCategories';
import ExploreNow from './ExploreNow';

// ============ Styled Components ============

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(4),
  flexWrap: 'wrap',
  gap: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
}));

const Title = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '2rem',
  color: theme.palette.text.primary,
  fontFamily: 'sans-serif',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.4rem',
  },
}));

const TabContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  backgroundColor: alpha(theme.palette.primary.main, 0.08),
  borderRadius: '50px',
  padding: '4px',
  [theme.breakpoints.down('sm')]: {
    overflowX: 'auto',
    flexWrap: 'nowrap',
    width: '100%',
    msOverflowStyle: 'none',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
}));

const TabButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'active',
})(({ theme, active }) => ({
  borderRadius: '50px',
  padding: '8px 24px',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.95rem',
  [theme.breakpoints.down('sm')]: {
    padding: '6px 14px',
    fontSize: '0.8rem',
    flexShrink: 0,
  },
  color: active ? '#fff' : theme.palette.text.secondary,
  backgroundColor: active ? theme.palette.primary.main : 'transparent',
  boxShadow: active ? `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}` : 'none',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: active ? theme.palette.primary.dark : alpha(theme.palette.primary.main, 0.08),
  },
}));

const CarouselWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  // Add padding on mobile to prevent buttons from overlapping content
  [theme.breakpoints.down('sm')]: {
    paddingLeft: '40px',
    paddingRight: '40px',
  },
  [theme.breakpoints.up('sm')]: {
    paddingLeft: '50px',
    paddingRight: '50px',
  },
}));

const CardsContainer = styled(Box)({
  display: 'flex',
  gap: '24px',
  overflowX: 'auto',
  scrollBehavior: 'smooth',
  padding: '10px 0',
  width: '100%',
  msOverflowStyle: 'none',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
});

const TourCard = styled(Box)(({ theme }) => ({
  minWidth: '300px',
  width: '300px',
  [theme.breakpoints.down('sm')]: {
    minWidth: '250px',  // Reduced from 260px for better mobile display
    width: '250px',      // Reduced from 260px for better mobile display
  },
  borderRadius: '16px',
  overflow: 'hidden',
  position: 'relative',
  cursor: 'pointer',
  flexShrink: 0,
  backgroundColor: '#fff',
  border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: `0 16px 48px ${alpha(theme.palette.common.black, 0.12)}`,
  },
}));

const CardImageWrapper = styled(Box)({
  position: 'relative',
  width: '100%',
  height: '200px',
  overflow: 'hidden',
});

const CardImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  transition: 'transform 0.5s ease',
  '&:hover': {
    transform: 'scale(1.08)',
  },
});

const CardContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

const CardTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '1.1rem',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.95rem',  // Slightly smaller for mobile
  },
  color: theme.palette.text.primary,
  lineHeight: 1.3,
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
}));

const CardMeta = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  color: theme.palette.text.secondary,
  fontSize: '0.85rem',
}));

const NavButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  width: '44px',
  height: '44px',
  position: 'absolute',
  zIndex: 3,
  boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
  },
  [theme.breakpoints.down('sm')]: {
    width: '36px',
    height: '36px',
  },
}));

const TrendingDestinations = () => {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTab, setSelectedTab] = useState('Domestic');

  // Dynamic categories from backend
  const { categories: CATEGORIES } = useCategories(true); // includeAll = true adds 'All'

  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const theme = useTheme();

  // Initial load
  useEffect(() => {
    fetchTours('Domestic', '');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTours = async (category, search = '') => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      if (category && category !== 'All') params.append('category', category);
      if (search) params.append('search', search);
      const url = `/tours${params.toString() ? `?${params.toString()}` : ''}`;
      const { data } = await API.get(url);
      setTours(Array.isArray(data) ? data : (data?.tours || []));
    } catch (err) {
      setError('Failed to load tours. Please try again later.');
      console.error('Error fetching tours:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (category) => {
    setSelectedTab(category);
    fetchTours(category, '');
  };

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = window.innerWidth > 600 ? 340 : 260;  // Adjusted scroll amount to match new card size
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  const getRating = (tour) => {
    if (tour.rating) return Number(tour.rating);
    if (tour._id) {
      const idStr = String(tour._id);
      const hash = idStr.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
      return 3.5 + (hash % 15) / 10;
    }
    return 4.0;
  };

  const getCapacity = (tour) => {
    if (tour.maxPeople) return `Up to ${tour.maxPeople} People`;
    return null;
  };

  const getLocation = (tour) => {
    return tour.destination || tour.location || null;
  };

  return (
    <Box sx={{ bgcolor: '#ffffff', minHeight: 'auto', py: { xs: 4, md: 8 } }}>
      <Container maxWidth="xl">

        {/* Header Title & Tabs */}
        <HeaderContainer>
          <Title>Trending Holiday Destinations</Title>
          <TabContainer>
            {CATEGORIES.filter(cat => {
              const isWildlife = cat.toLowerCase() === 'wildlife' || cat.toLowerCase() === 'wildlife tours';
              return !isWildlife;
            }).map((cat) => (
              <TabButton
                key={cat}
                active={selectedTab === cat}
                onClick={() => handleTabChange(cat)}
                disableElevation
              >
                {cat}
              </TabButton>
            ))}
          </TabContainer>
        </HeaderContainer>

        {/* Content */}
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="340px">
            <CircularProgress size={48} sx={{ color: theme.palette.primary.main }} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ my: 4, borderRadius: 2 }}>{error}</Alert>
        ) : tours.length === 0 ? (
          <Box textAlign="center" py={8}>
            <Typography variant="h6" color="text.secondary">
              No tours found for "{selectedTab}".
            </Typography>
          </Box>
        ) : (
          <CarouselWrapper>
            <NavButton
              onClick={() => scroll('left')}
              sx={{ 
                left: { xs: 0, sm: 0 },
                zIndex: 5 
              }}
              aria-label="Scroll Left"
            >
              <ArrowLeftIcon />
            </NavButton>

            <CardsContainer ref={scrollRef}>
              {tours.map((tour) => {
                const rating = getRating(tour);
                const capacity = getCapacity(tour);
                const location = getLocation(tour);

                return (
                  <TourCard
                    key={tour._id}
                    onClick={() => navigate(`/booking?tourId=${tour._id}`)}
                  >
                    <CardImageWrapper>
                      <CardImage
                        src={getImageUrl(tour.imageUrl) || FALLBACK_IMAGE}
                        alt={tour.title}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = FALLBACK_IMAGE;
                        }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: '50%',
                          background: 'linear-gradient(to top, rgba(0,0,0,0.4) 0%, transparent 100%)',
                          zIndex: 1,
                        }}
                      />
                    </CardImageWrapper>

                    <CardContent>
                      <CardTitle>{tour.title}</CardTitle>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Rating
                          value={rating}
                          readOnly
                          precision={0.5}
                          size="small"
                          sx={{ '& .MuiRating-iconFilled': { color: '#f59e0b' } }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          {rating.toFixed(1)}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                        {capacity && (
                          <CardMeta>
                            <PeopleIcon sx={{ fontSize: 16, opacity: 0.7 }} />
                            <Typography variant="caption" color="text.secondary">{capacity}</Typography>
                          </CardMeta>
                        )}
                        {location && (
                          <CardMeta>
                            <LocationIcon sx={{ fontSize: 16, opacity: 0.7 }} />
                            <Typography variant="caption" color="text.secondary">{location}</Typography>
                          </CardMeta>
                        )}
                      </Box>

                      <Button
                        variant="outlined"
                        size="small"
                        endIcon={<ArrowOutwardIcon fontSize="small" />}
                        sx={{
                          mt: 1,
                          borderRadius: '8px',
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '0.85rem',
                          borderColor: alpha(theme.palette.primary.main, 0.3),
                          color: 'primary.main',
                          '&:hover': {
                            borderColor: 'primary.main',
                            bgcolor: alpha(theme.palette.primary.main, 0.04),
                          },
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/booking?tourId=${tour._id}`);
                        }}
                      >
                        Book Now
                      </Button>
                    </CardContent>
                  </TourCard>
                );
              })}
            </CardsContainer>

            <NavButton
              onClick={() => scroll('right')}
              sx={{ 
                right: { xs: 0, sm: 0 },
                zIndex: 5 
              }}
              aria-label="Scroll Right"
            >
              <ArrowRightIcon />
            </NavButton>
          </CarouselWrapper>
        )}

        {/* Explore Now */}
        <ExploreNow onClick={() => navigate(`/tours?category=${selectedTab}`)} />

      </Container>
    </Box>
  );
};

export default TrendingDestinations;